'use client';

import {
  requestLocationHotelPlaceholder,
  requestLocationHotelTitle,
  requestLocationLocationDescription,
  requestLocationPrivateAddressPlaceholder,
  requestLocationPrivateAddressTitle,
  requestLocationTravelPlaceholder,
  requestLocationTravelTitle,
  requestLocationUnknownPlaceholder,
  requestLocationUnknownTitle,
  wizardNext,
  wizardPrev,
} from '@bells/i18n';
import { AttendantSearchSingleResponse } from '@bells/schema/models/attendant';
import { RequestLocationType } from '@bells/schema/models/mapbox';
import { Button } from '@bells/ui/components/button';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@bells/ui/components/dialog';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@bells/ui/components/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { RequestLocationResults } from './request-location';

import { useRequest } from '@/components/girl/request-context';
import { useLanguage } from '@bells/core/i18n';
import {
  RequestFormSchema,
  type RequestForm,
} from '@bells/schema/models/request';

export const StepChooseAddress = ({
  data,
  resolve,
  goBack,
}: {
  data: AttendantSearchSingleResponse;
  resolve?: () => void;
  goBack?: () => void;
}) => {
  const request = useRequest();
  const { language } = useLanguage();

  const form = useForm<
    Pick<RequestForm, 'address'> & {
      startDate: (string | Date)[];
    }
  >({
    resolver: zodResolver(
      RequestFormSchema.pick({
        address: true,
      })
    ),

    mode: 'all',
    defaultValues: {
      address: request.values.address,
    },
  });

  const addressWatch = form.watch('address');

  useEffect(() => {
    request.setValues({ address: addressWatch });
  }, [addressWatch]);

  useEffect(() => {
    form.resetField('address');
  }, [request.values.location]);

  const placeholder = useMemo(() => {
    if (request.values.location == RequestLocationType.HOTEL) {
      return requestLocationHotelPlaceholder();
    } else if (request.values.location == RequestLocationType.PRIVATE_ADDRESS) {
      return requestLocationPrivateAddressPlaceholder();
    } else if (request.values.location == RequestLocationType.TRAVEL) {
      return requestLocationTravelPlaceholder();
    } else if (request.values.location == RequestLocationType.UNKNOWN) {
      return requestLocationUnknownPlaceholder();
    }
  }, [request.values.location]);

  const title = useMemo(() => {
    if (request.values.location == RequestLocationType.HOTEL) {
      return requestLocationHotelTitle();
    } else if (request.values.location == RequestLocationType.PRIVATE_ADDRESS) {
      return requestLocationPrivateAddressTitle();
    } else if (request.values.location == RequestLocationType.TRAVEL) {
      return requestLocationTravelTitle();
    } else if (request.values.location == RequestLocationType.UNKNOWN) {
      return requestLocationUnknownTitle();
    }
  }, [request.values.location]);

  const submit = form.handleSubmit((val) => {
    request.setValues(val);
    if (resolve) {
      resolve();
    }
  });

  return (
    <>
      <DialogHeader className='p-6 pb-0'>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {requestLocationLocationDescription()}
        </DialogDescription>
      </DialogHeader>
      <div className='w-full flex-1 overflow-y-auto p-6 lg:max-h-[60vh]'>
        <FormProvider {...form}>
          <form
            className='flex w-full flex-col gap-4 lg:gap-6'
            onSubmit={submit}
          >
            <FormField
              control={form.control}
              name='address'
              render={({ field }) => (
                <FormItem className={'w-full'}>
                  <FormControl>
                    <div className='lg:rounded-lg'>
                      <RequestLocationResults
                        selected={
                          field.value
                            ? `${field.value.name}-${field.value.lng}-${field.value.lat}`
                            : undefined
                        }
                        placeholder={placeholder}
                        onSelect={field.onChange}
                        type={
                          request.values.location ?? RequestLocationType.HOTEL
                        }
                        language={language}
                      ></RequestLocationResults>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </FormProvider>
      </div>

      <DialogFooter className='gap-2 p-6 pt-0'>
        <Button type='button' onClick={goBack} variant={'outline'}>
          {wizardPrev()}
        </Button>
        <Button
          type='submit'
          onClick={submit}
          disabled={!form.formState.isValid}
        >
          {wizardNext()}
        </Button>
      </DialogFooter>
    </>
  );
};
